import type { IEntriDnsRecord } from '@feathr/blackbox';

export interface IEntriConfig {
  applicationId: string;
  dnsRecords: IEntriDnsRecord[];
  prefilledDomain: string;
  token: string;
}

interface IEntri {
  connectDomain: (config: IEntriConfig) => void;
}

export type TEntriSetupType =
  | 'automatic'
  | 'manual'
  | 'purchase'
  | 'semiautomatic'
  | 'sharedLogin'
  | null;

export interface IEntriCloseDetail {
  domain: string;
  error?: { code: string; details?: string; title: string };
  lastStatus: string;
  manualScreenDisabled?: boolean;
  provider: string | null;
  setupType: TEntriSetupType;
  success: boolean;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    entri?: IEntri;
  }
}

export const ENTRI_SCRIPT_SRC = 'https://cdn.goentri.com/entri.js';
export const ENTRI_CLOSE_EVENT = 'onEntriClose';

// Only an in-flight load is cached; once loaded, window.entri is the source of truth.
let pendingLoad: Promise<IEntri> | undefined;

/** Injects the Entri SDK script on first use and resolves with window.entri; a failed load can be retried. */
export function loadEntriSdk(): Promise<IEntri> {
  if (window.entri) {
    return Promise.resolve(window.entri);
  }
  pendingLoad ??= new Promise(function injectScript(resolve, reject): void {
    const script = document.createElement('script');
    script.async = true;
    script.src = ENTRI_SCRIPT_SRC;
    script.onload = function handleLoad(): void {
      pendingLoad = undefined;
      if (window.entri) {
        resolve(window.entri);
      } else {
        reject(new Error('Entri SDK loaded but window.entri is unavailable.'));
      }
    };
    script.onerror = function handleError(): void {
      pendingLoad = undefined;
      script.remove();
      reject(new Error('Failed to load the Entri SDK.'));
    };
    document.head.appendChild(script);
  });
  return pendingLoad;
}

let activeCloseListener: ((event: Event) => void) | undefined;

/** Registers the one-shot handler for Entri's close event, replacing any handler still waiting. */
export function onEntriClosed(handler: (detail: IEntriCloseDetail) => void): void {
  if (activeCloseListener) {
    window.removeEventListener(ENTRI_CLOSE_EVENT, activeCloseListener);
  }
  activeCloseListener = function handleEntriClose(event: Event): void {
    activeCloseListener = undefined;
    handler((event as CustomEvent<IEntriCloseDetail>).detail);
  };
  window.addEventListener(ENTRI_CLOSE_EVENT, activeCloseListener, { once: true });
}
